import { useContext } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { EditorContextPanelContext } from '@confluence/editor-context-panel/entry-points/EditorContextPanelContext.ts';
import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs.ts';

import {
	ANALYTICS_SOURCE,
	ANALYTICS_ORIGIN,
	ANALYTICS_SUBJECT,
	ANALYTICS_SUBJECT_ID,
} from '../constants/snippetAnalytics.ts';
import { SnippetEditorModal } from '../modals/SnippetEditorModal.tsx';
import { DeleteSnippetModal } from '../modals/DeleteSnippetModal.tsx';
import { SnippetEditorModalMode, SnippetScope, type Snippet } from '../snippetTypes.ts';

export type SnippetActionHandlersOptions = {
	contentId: string;
	spaceId: string;
	spaceKey: string;
};

export const useSnippetActionHandlers = ({
	contentId,
	spaceId,
	spaceKey,
}: SnippetActionHandlersOptions) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { showModal } = useDialogs();
	const { editorAPI } = useContext(EditorContextPanelContext);

	const firePanelAction = (
		actionSubjectId: string,
		snippetId: string,
		scope: SnippetScope = SnippetScope.USER,
	) => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: ANALYTICS_SOURCE,
				action: 'clicked',
				actionSubject: ANALYTICS_SUBJECT.BUTTON,
				actionSubjectId,
				type: 'ui',
				attributes: {
					origin: ANALYTICS_ORIGIN.SNIPPET_PANEL,
					snippetId,
					scope,
				},
			},
		}).fire();
	};

	const handleEdit = (snippet: Snippet) => {
		firePanelAction(ANALYTICS_SUBJECT_ID.EDIT_SNIPPET, snippet.snippetId);
		showModal(SnippetEditorModal, {
			mode: SnippetEditorModalMode.EDIT,
			snippet,
			editorAPI,
			contentId,
			spaceId,
			spaceKey,
		});
	};

	const handleDuplicate = (snippet: Snippet) => {
		firePanelAction(ANALYTICS_SUBJECT_ID.DUPLICATE_SNIPPET, snippet.snippetId, snippet.scope);
		showModal(SnippetEditorModal, {
			mode: SnippetEditorModalMode.DUPLICATE,
			snippet,
			editorAPI,
			contentId,
			spaceId,
			spaceKey,
		});
	};

	const handleDelete = (snippet: Snippet) => {
		firePanelAction(ANALYTICS_SUBJECT_ID.DELETE_SNIPPET, snippet.snippetId);
		showModal(DeleteSnippetModal, { snippet, editorAPI });
	};

	return { handleEdit, handleDuplicate, handleDelete };
};
